import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FormFieldLabel } from '../atoms/FormTextInput'
import SearchSvg from '../other/svg/SearchSvg'
import TrianglesMiniSvg from '../other/svg/TrianglesMiniSvg'
import XMarkSvg from '../other/svg/XMarkSvg'
import { RootReducer } from 'src/redux/rootReducer'
import {
  FETCH_MARKETPLACE_OFFERS,
  MarketplaceOffer,
  SET_CURRENT_PAGE,
  SET_SEARCH_TERM
} from 'src/redux/marketplaceOffersReducer'

interface PurchasedOffersPickerProps {
  selectedOffer: MarketplaceOffer | null
  onSelect: (offer: MarketplaceOffer) => void
  onClear: () => void
}

const fieldShadow = '0px 0px 0px 1px rgba(0,0,0,0.08), 0px 1px 2px 0px rgba(0,0,0,0.12)'
const focusShadow = '0px 0px 0px 1px rgba(37,99,235,1.00), 0px 0px 0px 4px rgba(37,99,235,0.20)'
const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const yearFromDate = (value: string | null): string => {
  if (value === null || value.length < 4) {
    return ''
  }
  return value.slice(0, 4)
}

const formatMileage = (value: number | null): string => {
  if (value === null) {
    return ''
  }
  return `${value.toLocaleString('pl-PL')} km`
}

const formatOfferDate = (value: string | null): string => {
  if (value === null || value.length === 0) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${date.getFullYear()}`
}

const offerSubtitle = (offer: MarketplaceOffer): string => {
  return [yearFromDate(offer.first_registration), formatMileage(offer.total_mileage), offer.vin]
    .filter((part) => part !== null && part.length > 0)
    .join(' · ')
}

const PurchasedOffersPicker = ({ selectedOffer, onSelect, onClear }: PurchasedOffersPickerProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(selectedOffer === null)
  const marketplaceOffers = useSelector((state: RootReducer) => state.marketplaceOffersReducer.marketplaceOffers)
  const currentPage = useSelector((state: RootReducer) => state.marketplaceOffersReducer.currentPage)
  const loading = useSelector((state: RootReducer) => state.marketplaceOffersReducer.loading)
  const searchTerm = useSelector((state: RootReducer) => state.marketplaceOffersReducer.searchTerm)

  useEffect(() => {
    dispatch(FETCH_MARKETPLACE_OFFERS())
  }, [dispatch, currentPage])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (wrapperRef.current === null || wrapperRef.current.contains(event.target as Node)) {
        return
      }
      setIsOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-1" ref={wrapperRef}>
      <div className="self-stretch flex flex-col justify-start items-start gap-2">
        <FormFieldLabel label={t('my_vehicles.choose_vehicle')} />
        <div
          className={`self-stretch px-3 bg-backgrounds-bg-field-component rounded-md inline-flex justify-start items-center gap-2 overflow-hidden ${
            selectedOffer === null ? 'h-11 py-2.5' : 'h-14 py-2'
          }`}
          style={{ boxShadow: isOpen ? focusShadow : fieldShadow }}>
          {selectedOffer === null ? (
            <>
              <SearchSvg className="size-6 text-icon-secondary shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                autoComplete="off"
                onFocus={() => {
                  setIsOpen(true)
                }}
                onChange={(event) => {
                  dispatch(SET_SEARCH_TERM(event.target.value))
                  dispatch(SET_CURRENT_PAGE(1))
                  dispatch(FETCH_MARKETPLACE_OFFERS())
                  setIsOpen(true)
                }}
                className="flex-1 h-full bg-transparent outline-none text-foregrounds-fg-base text-sm font-medium font-Montserrat leading-5"
              />
            </>
          ) : (
            <>
              <img
                src={selectedOffer.thumb_url ?? '/assets/images/no_photo@x2.png'}
                alt=""
                className="w-12 h-9 rounded-sm object-cover shrink-0"
              />
              <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5 min-w-0">
                <div className="self-stretch text-zinc-900 text-sm font-semibold font-Montserrat leading-5 truncate">
                  {selectedOffer.brand_name} {selectedOffer.model}
                </div>
                <div className="self-stretch text-zinc-500 text-xs font-medium font-Montserrat leading-4 truncate">
                  {offerSubtitle(selectedOffer)}
                </div>
              </div>
              <button
                type="button"
                className="size-8 flex justify-center items-center shrink-0"
                aria-label="Clear"
                onClick={() => {
                  onClear()
                  setIsOpen(true)
                }}>
                <XMarkSvg className="size-[15px] text-buttons-text-secondary" />
              </button>
            </>
          )}
          <button
            type="button"
            className="size-6 flex justify-center items-center overflow-hidden shrink-0"
            onClick={() => {
              if (isOpen) {
                setIsOpen(false)
                inputRef.current?.blur()
                return
              }
              setIsOpen(true)
              inputRef.current?.focus()
            }}>
            <TrianglesMiniSvg className="size-3.5 text-foregrounds-fg-muted" />
          </button>
        </div>
      </div>
      {isOpen && (
        <div
          className="self-stretch relative bg-backgrounds-bg-component rounded-lg flex flex-col justify-start items-start overflow-hidden"
          style={{ boxShadow: menuShadow }}>
          <div className="self-stretch p-1 max-h-96 overflow-y-auto flex flex-col justify-start items-start">
            {loading && (
              <div className="self-stretch py-8 text-center text-foregrounds-fg-muted text-sm font-medium font-Montserrat">
                {t('my_vehicles.loading_offers')}
              </div>
            )}
            {!loading && marketplaceOffers.length === 0 && (
              <div className="self-stretch py-8 text-center text-foregrounds-fg-muted text-sm font-medium font-Montserrat">
                {t('my_vehicles.no_purchased')}
              </div>
            )}
            {!loading &&
              marketplaceOffers.map((offer) => (
                <button
                  type="button"
                  key={offer.id}
                  onClick={() => {
                    onSelect(offer)
                    setIsOpen(false)
                  }}
                  className={`self-stretch h-14 px-3 py-2 rounded-md inline-flex justify-start items-start gap-2.5 hover:bg-backgrounds-bg-field-component-hover text-left ${
                    selectedOffer?.id === offer.id
                      ? 'bg-backgrounds-bg-component-hover'
                      : 'bg-backgrounds-bg-field-component'
                  }`}>
                  <img
                    src={offer.thumb_url ?? '/assets/images/no_photo@x2.png'}
                    alt=""
                    className="w-12 h-9 rounded-sm object-cover shrink-0"
                  />
                  <div className="flex-1 flex justify-start items-start min-w-0">
                    <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5 min-w-0">
                      <div className="self-stretch text-zinc-900 text-sm font-semibold font-Montserrat leading-5 truncate">
                        {offer.brand_name} {offer.model}
                      </div>
                      <div className="self-stretch text-zinc-500 text-xs font-medium font-Montserrat leading-4 truncate">
                        {offerSubtitle(offer)}
                      </div>
                    </div>
                    <div className="text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-3 shrink-0 pl-2">
                      {formatOfferDate(offer.archived_at ?? offer.published_at)}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PurchasedOffersPicker
